import { useState } from 'react'
import { api, type DateSource, type LayoutProfile } from '../api'

/**
 * Where a mapped layout's effective date comes from — TECHNICAL_DESIGN.md §5.2, §8.3.
 *
 * **Why a mapped layout can be edited at all.** Confirming a layout was a
 * one-way door: the shape left the *waiting for a mapping* queue, and that queue
 * was the only route to the mapper. A profile confirmed before the effective
 * date was asked for therefore says nothing about it, refuses every document
 * that arrives in its shape, and could not be corrected from anywhere in the
 * application — the client's guidelines simply stopped.
 *
 * **It edits one field and carries the rest through.** The row mapping is a
 * judgement about a document somebody had in front of them; re-opening it
 * without the document would be asking them to confirm a mapping they cannot
 * see. The date binding is a fact about the client's file that does not need the
 * file to answer, which is why it is the one thing safe to change from a list.
 *
 * The write supersedes rather than updates, exactly as confirming does. Limits
 * were imported under the old binding and their dates came from it.
 */
export function EffectiveDateBinding({
  profile,
  onDone,
}: {
  profile: LayoutProfile
  onDone: () => void
}) {
  const [source, setSource] = useState<DateSource>(
    profile.effective_date_source,
  )
  const [cell, setCell] = useState(profile.effective_date_cell ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const answered = source !== 'cell' || cell.trim() !== ''

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.rebindEffectiveDate(profile.id, source, cell.trim() || null)
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shape-mapper" data-testid="effective-date-binding">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="note">
        Documents arriving in this shape are read without asking anyone, so the
        effective date has to come from somewhere the layout names. It is when
        the change takes effect for the client — not when the mail arrived, which
        can be months earlier.
      </p>

      <fieldset className="stacked">
        <legend>When do these guidelines take effect?</legend>
        <label className="choice">
          <input
            type="radio"
            name={`date-source-${profile.id}`}
            checked={source === 'cell'}
            onChange={() => setSource('cell')}
          />
          The date is in the document, at cell
          <input
            className="cell"
            value={cell}
            disabled={source !== 'cell'}
            onChange={(e) => setCell(e.target.value)}
            placeholder="B2"
            title="The cell reference as it appears in the client's own file — B2, D1."
          />
        </label>
        <label className="choice">
          <input
            type="radio"
            name={`date-source-${profile.id}`}
            checked={source === 'arrival'}
            onChange={() => setSource('arrival')}
          />
          This layout carries no date — its documents take effect on the day they
          arrive
        </label>
        <label className="choice">
          <input
            type="radio"
            name={`date-source-${profile.id}`}
            checked={source === 'document'}
            onChange={() => setSource('document')}
          />
          No date in the file — I give each document’s date
        </label>
        {source === 'document' && (
          <p className="note">
            Each document in this shape waits under <strong>Waiting for a date</strong>{' '}
            until someone gives the day it takes effect, then it is read.
          </p>
        )}
        {source === 'arrival' && (
          <p className="warn">
            If this client ever sends guidelines ahead of time — limits agreed
            now that start next quarter — they would bind from the day the mail
            landed, which is early.
          </p>
        )}
      </fieldset>

      <div className="actions">
        <button
          disabled={busy || !answered}
          title={answered ? undefined : 'Name the cell, or choose the other answer.'}
          onClick={() => void save()}
        >
          Save
        </button>
        <span className="note">
          Documents already refused for want of a date are on{' '}
          <strong>Refused items</strong>. Saving this does not re-read them —
          send them again, or clear them from that queue.
        </span>
      </div>
    </div>
  )
}
